import type { FuturesSide } from '@/lib/api/history/profile-types';
import { createHttpClient, type HttpClient } from '@/lib/api/httpClient';

// futures-trading-service — cookie-authed, same as the trade zone clients.
const BASE_URL = process.env.NEXT_PUBLIC_FUTURES_TRADING_SERVICE_URL || '';

const http = createHttpClient({ baseUrl: BASE_URL });

/** Trimmed position wire shape — only what the CROSS risk check, the popover and the header menu read. */
export interface PositionDto {
  id: string;
  /** pair id */
  pi: number;
  /** side */
  s: FuturesSide;
  /** size (base; USD contracts for inverse) */
  sz: number;
  /** entry price */
  e: number;
  /** margin mode */
  mmd: string;
  /** initial margin (settle coin) */
  im: number;
  /** leverage */
  l: number;
  /** unrealized PnL (settle coin) */
  upn: number;
}

/** One margin tier on the wire (`mt[i]`), ascending by notional. */
interface MarginTierDto {
  mxn: number | null;
  /** maintenance-margin rate */
  mm: string | number;
  /** cumulative maintenance deduction */
  cm: string | number;
}

/** Trimmed pair wire shape (`ps[i]` of `/trade/pairs`). */
interface PairDto {
  gid: number;
  /** base currency */
  bc: string;
  /** quote currency */
  qc: string;
  /** contract type (casing varies on the wire) */
  ct: string;
  /** base maintenance-margin rate */
  mmr?: string | number | null;
  /** taker fee of the default group — the liquidation fee equals the taker fee */
  tkf?: string | number | null;
  /** liquidation protection rate */
  lpr?: string | number | null;
  mt?: MarginTierDto[];
}

/** Trimmed `/trade/account` — the user's group taker fee (`pf` = per-pair overrides). */
interface AccountFeesDto {
  tkf?: string | number;
  pf?: Record<string, { tkf: string | number }>;
}

/** Aggregate CROSS exposure settling in one coin (all settle-coin denominated). */
export interface CrossCoinRisk {
  /** Σ unrealized PnL of the open CROSS positions — equity = wallet balance + this. */
  unrealizedPnl: number;
  /** Liquidation trigger floor Σ(lpr×IM + takerFee×notional); 0 when no rates configured. */
  triggerFloor: number;
  /** Σ maintenance margin — the ratio fallback when no trigger rates are set. */
  maintenanceMargin: number;
}

function num(v: string | number | null | undefined): number {
  return v == null ? 0 : Number(v);
}

/** Settle coin of a pair: the quote currency, or the base one for inverse pairs. */
function settleCoinOf(contractType: string, base: string, quote: string): string {
  return (contractType.toUpperCase() === 'INVERSE_PERPETUAL' ? base : quote).toUpperCase();
}

/** The current user's open positions. */
export async function fetchOpenPositions(): Promise<PositionDto[]> {
  return (await http.get<PositionDto[]>('/api/v1/positions')) ?? [];
}

/** How many resting (`NEW`) orders the current user has. */
export async function fetchNewOrdersCount(): Promise<number> {
  const orders = await http.get<unknown[]>('/api/v1/orders', { query: { status: 'NEW' } });
  return orders?.length ?? 0;
}

/**
 * CROSS margin exposure per settle coin (upper-case symbol key) of the current
 * user's open positions. A coin present in the map is backing cross margin right
 * now — the Withdrawal / Convert dialogs warn before moving it. Mirrors the trade
 * zone's `useCrossRisk` math with the backend's own figures instead of live marks:
 * notional = IM × leverage (the backend convention), uPnL from the response.
 * Settle coin is the quote currency, or the base one for inverse pairs.
 */
export async function fetchCrossCollateralRisk(): Promise<Record<string, CrossCoinRisk>> {
  const positions = await fetchOpenPositions();
  const cross = positions.filter((p) => p.mmd === 'CROSS');
  if (cross.length === 0) return {};

  const [{ ps }, account] = await Promise.all([
    http.get<{ ps: PairDto[] }>('/api/v1/trade/pairs'),
    // A failed account falls back to the default-group fee — never drop the warning.
    http.get<AccountFeesDto>('/api/v1/trade/account').catch(() => undefined),
  ]);
  const pairById = new Map((ps ?? []).map((p) => [p.gid, p]));

  const risk: Record<string, CrossCoinRisk> = {};
  for (const p of cross) {
    const pair = pairById.get(p.pi);
    if (!pair) continue;
    const settle = settleCoinOf(pair.ct, pair.bc, pair.qc);
    const notional = p.im * p.l;
    // Tiered MM (`notional × mmRate − cumMaint`, continuous across tiers), else flat mmr.
    const tier = (pair.mt ?? []).find((t) => t.mxn == null || notional <= t.mxn) ?? pair.mt?.at(-1);
    const maint = tier
      ? Math.max(0, notional * num(tier.mm) - num(tier.cm))
      : notional * num(pair.mmr);
    const entry = (risk[settle] ??= { unrealizedPnl: 0, triggerFloor: 0, maintenanceMargin: 0 });
    entry.unrealizedPnl += p.upn;
    const liqFee = account?.pf?.[pair.gid]?.tkf ?? account?.tkf ?? pair.tkf;
    entry.triggerFloor += p.im * num(pair.lpr) + notional * num(liqFee);
    entry.maintenanceMargin += maint;
  }
  return risk;
}

/** The Crypto Trading tab of the balance popover, in settle-coin units. */
export interface FuturesSummary {
  openPositions: number;
  activeOrders: number;
  /** Σ initial margin and Σ unrealized PnL of the open positions per settle coin. */
  bySettleCoin: Record<string, { margin: number; unrealizedPnl: number }>;
}

const MARKETDATA_BASE_URL = process.env.NEXT_PUBLIC_FUTURES_MARKETDATA_SERVICE_URL || '';

const marketdataHttp = createHttpClient({ baseUrl: MARKETDATA_BASE_URL });

/** A public read made by the server render (the home Hot Futures card). */
export interface FuturesServerRead {
  /** Seconds Next's data cache may share the answer for — it is the same for everyone. */
  revalidate: number;
}

/**
 * How long the server render waits for one read. Past it the card renders
 * without that data and the browser reads it — a slow service must not hold the page.
 */
const SERVER_READ_TIMEOUT_MS = 2500;

/** The browser's client, or a server render's one bounded in time. */
function clientFor(browserClient: HttpClient, baseUrl: string, server?: FuturesServerRead) {
  return server ? createHttpClient({ baseUrl, timeoutMs: SERVER_READ_TIMEOUT_MS }) : browserClient;
}

/** A server read is shared through the data cache; the browser's are never cached. */
function cacheFor(server?: FuturesServerRead) {
  return server ? { next: { revalidate: server.revalidate } } : undefined;
}

/** Trimmed pair wire shape (`ps[i]` of `/trade/pairs`) for the web-zone pair lookups. */
interface FuturesPairDto {
  gid: number;
  /** symbol */
  nm: string;
  bc: string;
  qc: string;
  /** contract type (casing varies on the wire) */
  ct: string;
  /** tick size */
  ts: string | number;
  /** lot size */
  ls: string | number;
  /** status (casing varies on the wire) */
  st: string;
  /** admin trending flag */
  tr?: boolean;
  /** asset icon URL; `null` until the feed carries icons */
  ic?: string | null;
  /** max leverage */
  ml?: number;
  /** asset type: CRYPTO, TOKENIZED_EQUITY or TOKENIZED_COMMODITY (casing varies) */
  at: string;
  /** codes of the pair's `tags`; missing until the feed carries tags */
  tg?: string[];
}

/** `tags[i]` of `/trade/pairs`: an admin-made pair group, e.g. `ETF`. */
interface PairTagDto {
  code: string;
  /** English name; our copy goes by `code` */
  name: string;
  /** shown as a TradFi menu row */
  menu: boolean;
  order: number;
}

/** Trimmed `/candles/lastDay` row: last price and 24h change %. */
interface LastDayTickerDto {
  pi: number;
  c: string | number;
  chp: string | number;
}

/** Trimmed `/ws/ticker/24h` frame, one pair each — the change % key is `cp`, not REST's `chp`. */
interface WsTickerDto {
  pi: number;
  c: string | number;
  cp: string | number;
}

/** A futures pair's static metadata; `last`/`changePct` only on the search list. */
export interface FuturesPair {
  id: number;
  symbol: string;
  baseCurrency: string;
  quoteCurrency: string;
  /** Canonical upper-case, e.g. `LINEAR_PERPETUAL` / `INVERSE_PERPETUAL`. */
  contractType: string;
  /** Terminal path, locale-less: `/trade/{quote|inverse}/{symbol}`. */
  path: string;
  tickSize: number;
  lotSize: number;
  active: boolean;
  trending: boolean;
  /** The base asset's icon; `null` — draw a monogram. */
  icon: string | null;
  /** Highest leverage the pair allows (e.g. 100); `null` when the feed omits it. */
  maxLeverage: number | null;
  /** A tokenized stock or commodity, not a crypto asset. */
  isTradFi: boolean;
  /** Codes of the admin tags the pair is grouped under (`STOCK`, `ETF`, …); `[]` for crypto. */
  tags: string[];
  last?: number;
  changePct?: number;
}

export interface PairTicker {
  pairId: number;
  last: number;
  changePct: number;
  /** Epoch ms the socket delivered it; absent on a REST read. */
  receivedAt?: number;
}

function mapFuturesPair(dto: FuturesPairDto): FuturesPair {
  const contractType = dto.ct.toUpperCase();
  const quote = contractType === 'INVERSE_PERPETUAL' ? 'inverse' : dto.qc.toLowerCase();
  return {
    id: dto.gid,
    symbol: dto.nm,
    baseCurrency: dto.bc,
    quoteCurrency: dto.qc,
    contractType,
    path: `/trade/${quote}/${dto.nm}`,
    tickSize: Number(dto.ts),
    lotSize: Number(dto.ls),
    active: dto.st.toUpperCase() === 'ACTIVE',
    trending: dto.tr === true,
    icon: dto.ic || null,
    maxLeverage: dto.ml ?? null,
    isTradFi: dto.at.toUpperCase() !== 'CRYPTO',
    tags: dto.tg ?? [],
  };
}

/** A TradFi menu row: pairs whose `tags` hold its `code`. */
export interface PairTag {
  code: string;
  name: string;
}

export interface FuturesPairsList {
  /** All pairs (delisted included), in server order. */
  pairs: FuturesPair[];
  /** The TradFi menu's rows in menu order; `null` until an env's feed carries tags. */
  menuTags: PairTag[] | null;
}

export async function fetchFuturesPairs(server?: FuturesServerRead): Promise<FuturesPairsList> {
  const res = await clientFor(http, BASE_URL, server).get<{
    ps?: FuturesPairDto[];
    tags?: PairTagDto[];
  }>('/api/v1/trade/pairs', cacheFor(server));
  const menuTags =
    res.tags
      ?.filter((tag) => tag.menu)
      .sort((a, b) => a.order - b.order)
      .map(({ code, name }) => ({ code, name })) ?? null;
  return { pairs: (res.ps ?? []).map(mapFuturesPair), menuTags };
}

/** All-pairs 24h snapshot. A failed fetch yields no tickers, never an error. */
export async function fetchLastDayTickers(server?: FuturesServerRead): Promise<PairTicker[]> {
  try {
    const res = await clientFor(marketdataHttp, MARKETDATA_BASE_URL, server).get<{
      d?: LastDayTickerDto[];
    }>('/api/v1/candles/lastDay', cacheFor(server));
    return (res.d ?? []).map((t) => ({
      pairId: t.pi,
      last: Number(t.c),
      changePct: Number(t.chp),
    }));
  } catch {
    return [];
  }
}

/** A `/ws/ticker/24h` frame as a ticker; anything else on the socket (acks, pongs) yields `null`. */
export function parseTickerFrame(frame: unknown): PairTicker | null {
  const dto = frame as Partial<WsTickerDto> | null;
  if (typeof dto?.pi !== 'number' || dto.c == null || dto.cp == null) return null;
  return {
    pairId: dto.pi,
    last: Number(dto.c),
    changePct: Number(dto.cp),
    receivedAt: Date.now(),
  };
}

/** Trimmed `/ws/book/best` frame: `[price, qty]` per side; `pi` comes as a string. */
interface WsBestQuoteDto {
  pi: string | number;
  b?: [string | number, string | number] | null;
  a?: [string | number, string | number] | null;
}

/** A pair's top of book; a side is `undefined` while the book is one-sided. */
export interface BestQuote {
  pairId: number;
  bid: number | undefined;
  ask: number | undefined;
}

function levelPrice(level: WsBestQuoteDto['b']): number | undefined {
  const price = Number(level?.[0]);
  return Number.isFinite(price) && price > 0 ? price : undefined;
}

/** A `/ws/book/best` frame as a quote; acks and pongs yield `null`. */
export function parseBestQuoteFrame(frame: unknown): BestQuote | null {
  const dto = frame as Partial<WsBestQuoteDto> | null;
  const pairId = Number(dto?.pi);
  if (dto?.pi == null || !Number.isFinite(pairId)) return null;
  return { pairId, bid: levelPrice(dto.b), ask: levelPrice(dto.a) };
}

/** `tickers` with each pair in `updates` replaced by (or, if new, added as) its live ticker. */
export function mergeTickers(
  tickers: PairTicker[],
  updates: ReadonlyMap<number, PairTicker>,
): PairTicker[] {
  const byId = new Map(tickers.map((ticker) => [ticker.pairId, ticker]));
  for (const [pairId, ticker] of updates) byId.set(pairId, ticker);
  return Array.from(byId.values());
}

/** A REST read with the socket's tickers delivered after `startedAt` kept over it: those are newer. */
export function keepStreamedTickers(
  rest: PairTicker[],
  cached: PairTicker[] | undefined,
  startedAt: number,
): PairTicker[] {
  const streamed = (cached ?? []).filter((ticker) => (ticker.receivedAt ?? 0) > startedAt);
  return mergeTickers(rest, new Map(streamed.map((ticker) => [ticker.pairId, ticker])));
}

/** Trimmed `/candles/history` bar: open time (epoch ms) and close. */
interface CandleCloseDto {
  t: number;
  c: string | number;
}

/** A price at a moment, `time` in epoch seconds — what a sparkline draws. */
export interface PricePoint {
  time: number;
  value: number;
}

/** 24 hourly bars = the last day; finer bars turn a 64px sparkline into noise. */
const DAY_OF_HOURS = 24;

/** The pair's last day as hourly closes, oldest first. A failed fetch yields none. */
export async function fetchDayCloses(
  pairId: number,
  server?: FuturesServerRead,
): Promise<PricePoint[]> {
  try {
    const res = await clientFor(marketdataHttp, MARKETDATA_BASE_URL, server).get<{
      cs?: CandleCloseDto[];
    }>('/api/v1/candles/history', {
      query: { pairId, interval: '1h', limit: DAY_OF_HOURS },
      ...cacheFor(server),
    });
    return (res.cs ?? []).map((bar) => ({ time: Math.floor(bar.t / 1000), value: Number(bar.c) }));
  } catch {
    return [];
  }
}

/** ACTIVE pairs with their 24h ticker merged in, as the header search lists them. */
export function withTickers(pairs: FuturesPair[], tickers: PairTicker[]): FuturesPair[] {
  const tickerById = new Map(tickers.map((ticker) => [ticker.pairId, ticker]));
  return pairs
    .filter((pair) => pair.active)
    .map((pair) => {
      const ticker = tickerById.get(pair.id);
      return ticker ? { ...pair, last: ticker.last, changePct: ticker.changePct } : pair;
    });
}

/** The popover's Crypto Trading figures; positions on a pair missing from `pairs` are only counted. */
export function summarizeFutures(
  positions: PositionDto[],
  pairs: FuturesPair[],
  activeOrders: number,
): FuturesSummary {
  const pairById = new Map(pairs.map((pair) => [pair.id, pair]));
  const bySettleCoin: FuturesSummary['bySettleCoin'] = {};
  for (const p of positions) {
    const pair = pairById.get(p.pi);
    if (!pair) continue;
    const settle = settleCoinOf(pair.contractType, pair.baseCurrency, pair.quoteCurrency);
    const entry = (bySettleCoin[settle] ??= { margin: 0, unrealizedPnl: 0 });
    entry.margin += p.im;
    entry.unrealizedPnl += p.upn;
  }
  return { openPositions: positions.length, activeOrders, bySettleCoin };
}
